import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { formatNumber } from '../lib/format';
import { IconMaximize, IconNavigation, IconPin, IconZoomIn, IconZoomOut } from './icons';

/**
 * Karte der Tour in drei Ebenen.
 *
 *  - **Schematisch** – eigene Vektordarstellung ohne Kartendienst. Sie
 *    funktioniert offline, kostet keine Kachelabrufe und bleibt die
 *    Rückfallebene, wenn kein Schlüssel hinterlegt oder der Dienst gestört ist.
 *  - **Straße** und **Satellit** – Rasterkacheln über `/api/maps`. Der Server
 *    hält den Schlüssel und legt die Kacheln lokal ab; der Browser spricht
 *    nie direkt mit dem Kartenanbieter.
 *
 * Beide Kachelebenen und die Route liegen in derselben Web-Mercator-
 * Projektion – die Stopps sitzen daher exakt auf ihren Koordinaten und
 * verschieben sich beim Zoomen nicht gegeneinander.
 */

export interface MapStop {
  key: string;
  label: string;
  address?: string | null;
  lat: number;
  lng: number;
  position?: number;
  distanceFromPrev?: number;
}

type Layer = 'schematic' | 'streets' | 'satellite';

const TILE = 256;
const MIN_ZOOM = 3;
const STORAGE_KEY = 'ff.mapLayer';

/* ------------------------------------------------------------------ */
/* Projektion                                                          */
/* ------------------------------------------------------------------ */

/** Geokoordinate → Bildpunkt der Weltkarte auf der angegebenen Zoomstufe. */
function project(lat: number, lng: number, zoom: number) {
  const scale = TILE * 2 ** zoom;
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sin = Math.sin((clamped * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

/** Umkehrung von `project`. */
function unproject(x: number, y: number, zoom: number) {
  const scale = TILE * 2 ** zoom;
  const n = Math.PI - 2 * Math.PI * (y / scale);
  return {
    lng: (x / scale) * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
  };
}

/* ------------------------------------------------------------------ */
/* Komponente                                                          */
/* ------------------------------------------------------------------ */

export function RouteMap({
  stops,
  roundTrip,
  selectedKey,
  onSelect,
}: {
  stops: MapStop[];
  roundTrip: boolean;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}) {
  const points = useMemo(
    () => stops.filter((stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng)),
    [stops],
  );

  const [layer, setLayer] = useState<Layer>(
    () => (localStorage.getItem(STORAGE_KEY) as Layer) || 'schematic',
  );
  const [mapsConfig, setMapsConfig] = useState<{ available: boolean; layers: any[]; hint: string | null } | null>(null);

  useEffect(() => {
    api
      .get('/api/maps/config')
      .then(setMapsConfig)
      .catch(() => setMapsConfig({ available: false, layers: [], hint: null }));
  }, []);

  // Ohne Schlüssel auf die schematische Ansicht zurückfallen, statt eine
  // graue Fläche zu zeigen.
  useEffect(() => {
    if (mapsConfig && !mapsConfig.available && layer !== 'schematic') setLayer('schematic');
  }, [mapsConfig, layer]);

  useEffect(() => localStorage.setItem(STORAGE_KEY, layer), [layer]);

  const attribution = mapsConfig?.layers.find((entry) => entry.key === layer)?.attribution ?? null;

  return (
    <div className="route-map">
      <span className="route-map-status">
        <span className="live-dot" />
        {points.length > 0 ? `${points.length} Stopps` : 'Karte'}
      </span>

      {/* Ebenenumschalter – nur mit hinterlegtem Schlüssel vollständig. */}
      <div className="route-map-layers">
        <button className={`layer-tab ${layer === 'schematic' ? 'active' : ''}`} onClick={() => setLayer('schematic')}>
          Schematisch
        </button>
        {(mapsConfig?.layers ?? []).map((entry: any) => (
          <button
            key={entry.key}
            className={`layer-tab ${layer === entry.key ? 'active' : ''}`}
            onClick={() => setLayer(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {layer === 'schematic' ? (
        <SchematicMap stops={points} roundTrip={roundTrip} selectedKey={selectedKey} onSelect={onSelect} />
      ) : (
        <TileMap
          key={layer}
          layer={layer}
          stops={points}
          roundTrip={roundTrip}
          selectedKey={selectedKey}
          onSelect={onSelect}
        />
      )}

      {attribution && <span className="route-map-attribution">{attribution}</span>}

      {mapsConfig && !mapsConfig.available && mapsConfig.hint && (
        <span className="route-map-attribution">{mapsConfig.hint}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Kachelkarte                                                         */
/* ------------------------------------------------------------------ */

function TileMap({
  layer,
  stops,
  roundTrip,
  selectedKey,
  onSelect,
}: {
  layer: Layer;
  stops: MapStop[];
  roundTrip: boolean;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 460 });
  const [view, setView] = useState({ lat: 52.3759, lng: 9.732, zoom: 12 });
  const [dragging, setDragging] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; lat: number; lng: number } | null>(null);

  /** Ausschnitt so wählen, dass alle Stopps hineinpassen. */
  const fit = useCallback(
    (width: number, height: number) => {
      if (stops.length === 0 || width === 0) return;
      if (stops.length === 1) {
        setView({ lat: stops[0].lat, lng: stops[0].lng, zoom: 14 });
        return;
      }

      const minLat = Math.min(...stops.map((s) => s.lat));
      const maxLat = Math.max(...stops.map((s) => s.lat));
      const minLng = Math.min(...stops.map((s) => s.lng));
      const maxLng = Math.max(...stops.map((s) => s.lng));
      const padding = 70;

      // Größte Zoomstufe suchen, auf der die Ausdehnung noch hineinpasst.
      let zoom = 18;
      for (; zoom > MIN_ZOOM; zoom -= 1) {
        const a = project(maxLat, minLng, zoom);
        const b = project(minLat, maxLng, zoom);
        if (b.x - a.x <= width - 2 * padding && b.y - a.y <= height - 2 * padding) break;
      }

      const center = unproject(
        (project(maxLat, minLng, zoom).x + project(minLat, maxLng, zoom).x) / 2,
        (project(maxLat, minLng, zoom).y + project(minLat, maxLng, zoom).y) / 2,
        zoom,
      );
      setView({ ...center, zoom });
    },
    [stops],
  );

  // Größe des Containers verfolgen – die Kachelmenge hängt daran.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: Math.round(rect.width), height: Math.round(rect.height) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Beim Wechsel der Stoppmenge neu einpassen.
  useEffect(() => {
    if (size.width > 0) fit(size.width, size.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops.length, size.width, size.height]);

  const centerPx = project(view.lat, view.lng, view.zoom);
  const topLeft = { x: centerPx.x - size.width / 2, y: centerPx.y - size.height / 2 };
  const worldTiles = 2 ** view.zoom;

  /** Sichtbare Kacheln. */
  const tiles = useMemo(() => {
    if (size.width === 0) return [];
    const list: { key: string; url: string; left: number; top: number }[] = [];
    const x0 = Math.floor(topLeft.x / TILE);
    const x1 = Math.floor((topLeft.x + size.width) / TILE);
    const y0 = Math.floor(topLeft.y / TILE);
    const y1 = Math.floor((topLeft.y + size.height) / TILE);

    for (let ty = y0; ty <= y1; ty += 1) {
      if (ty < 0 || ty >= worldTiles) continue; // oberhalb/unterhalb der Weltkarte
      for (let tx = x0; tx <= x1; tx += 1) {
        // Waagerecht umlaufen, damit der Datumsgrenze keine Lücke folgt.
        const wrapped = ((tx % worldTiles) + worldTiles) % worldTiles;
        list.push({
          key: `${view.zoom}-${tx}-${ty}`,
          url: `/api/maps/tiles/${layer}/${view.zoom}/${wrapped}/${ty}`,
          left: tx * TILE - topLeft.x,
          top: ty * TILE - topLeft.y,
        });
      }
    }
    return list;
  }, [layer, view.zoom, topLeft.x, topLeft.y, size.width, size.height, worldTiles]);

  const screen = (stop: MapStop) => {
    const p = project(stop.lat, stop.lng, view.zoom);
    return { x: p.x - topLeft.x, y: p.y - topLeft.y };
  };

  /** Zoomen mit Ankerpunkt unter dem Mauszeiger. */
  const zoomBy = (delta: number, anchorX?: number, anchorY?: number) => {
    setView((current) => {
      const next = Math.max(MIN_ZOOM, Math.min(19, current.zoom + delta));
      if (next === current.zoom) return current;

      const ax = anchorX ?? size.width / 2;
      const ay = anchorY ?? size.height / 2;
      const currentCenter = project(current.lat, current.lng, current.zoom);
      const currentTopLeft = { x: currentCenter.x - size.width / 2, y: currentCenter.y - size.height / 2 };
      const anchorGeo = unproject(currentTopLeft.x + ax, currentTopLeft.y + ay, current.zoom);

      const anchorPx = project(anchorGeo.lat, anchorGeo.lng, next);
      const newCenter = unproject(anchorPx.x - ax + size.width / 2, anchorPx.y - ay + size.height / 2, next);
      return { ...newCenter, zoom: next };
    });
  };

  const active = stops.find((stop) => stop.key === (hovered ?? selectedKey)) ?? null;

  return (
    <>
      <div className="route-map-toolbar">
        <button className="btn sm icon-only" title="Vergrößern" onClick={() => zoomBy(1)}>
          <IconZoomIn width={15} height={15} />
        </button>
        <button className="btn sm icon-only" title="Verkleinern" onClick={() => zoomBy(-1)}>
          <IconZoomOut width={15} height={15} />
        </button>
        <button className="btn sm icon-only" title="Alle Stopps zeigen" onClick={() => fit(size.width, size.height)}>
          <IconMaximize width={15} height={15} />
        </button>
      </div>

      <div
        ref={containerRef}
        className={`route-map-canvas tiles ${dragging ? 'dragging' : ''}`}
        onPointerDown={(event) => {
          setDragging(true);
          dragRef.current = { x: event.clientX, y: event.clientY, lat: view.lat, lng: view.lng };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const origin = dragRef.current;
          if (!dragging || !origin) return;
          const startPx = project(origin.lat, origin.lng, view.zoom);
          const moved = unproject(
            startPx.x - (event.clientX - origin.x),
            startPx.y - (event.clientY - origin.y),
            view.zoom,
          );
          setView((current) => ({ ...current, ...moved }));
        }}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        onWheel={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          zoomBy(event.deltaY < 0 ? 1 : -1, event.clientX - rect.left, event.clientY - rect.top);
        }}
      >
        <div className="tile-plane">
          {tiles.map((tile) => (
            <img
              key={tile.key}
              src={tile.url}
              alt=""
              draggable={false}
              width={TILE}
              height={TILE}
              style={{ left: tile.left, top: tile.top }}
              onError={(event) => {
                // Fehlende Kachel still lassen – ein Bruchsymbol wäre lauter
                // als das Loch, das es füllen soll.
                event.currentTarget.style.visibility = 'hidden';
              }}
            />
          ))}
        </div>

        {/* Route und Stopps als Überlagerung in derselben Projektion. */}
        <svg className="tile-overlay" width={size.width} height={size.height}>
          <defs>
            <marker id="tile-arrow" viewBox="0 0 10 10" refX="24" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 1 L 10 5 L 0 9 z" fill="var(--primary)" />
            </marker>
          </defs>

          {stops.slice(0, -1).map((stop, index) => {
            const from = screen(stop);
            const to = screen(stops[index + 1]);
            return (
              <g key={`leg-${stop.key}`}>
                <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#fff" strokeOpacity={0.8} strokeWidth={7} strokeLinecap="round" />
                <line
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="var(--primary)"
                  strokeWidth={3.5}
                  strokeLinecap="round"
                  markerEnd="url(#tile-arrow)"
                />
              </g>
            );
          })}

          {roundTrip && stops.length > 2 && (
            <line
              x1={screen(stops[stops.length - 1]).x}
              y1={screen(stops[stops.length - 1]).y}
              x2={screen(stops[0]).x}
              y2={screen(stops[0]).y}
              stroke="var(--primary)"
              strokeWidth={2.5}
              strokeDasharray="4, 7"
              strokeLinecap="round"
              opacity={0.85}
            />
          )}

          {stops.map((stop, index) => {
            const p = screen(stop);
            const isSelected = selectedKey === stop.key || hovered === stop.key;
            const isStart = index === 0;
            const isEnd = index === stops.length - 1 && !roundTrip;
            const fill = isStart ? 'var(--success)' : isEnd ? 'var(--danger)' : 'var(--primary)';
            return (
              <g
                key={stop.key}
                transform={`translate(${p.x}, ${p.y})`}
                style={{ cursor: 'pointer' }}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect?.(stop.key === selectedKey ? null : stop.key);
                }}
                onMouseEnter={() => setHovered(stop.key)}
                onMouseLeave={() => setHovered(null)}
              >
                <circle r={isSelected ? 15 : 12.5} fill={fill} stroke="#fff" strokeWidth="2.5" />
                <text textAnchor="middle" y="4" fontSize={isSelected ? 11.5 : 10} fontWeight="800" fill="#fff">
                  {index + 1}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {active && <StopDetail stop={active} stops={stops} roundTrip={roundTrip} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Schematische Ansicht                                                */
/* ------------------------------------------------------------------ */

const WIDTH = 820;
const HEIGHT = 500;
const PADDING = 70;

function SchematicMap({
  stops,
  roundTrip,
  selectedKey,
  onSelect,
}: {
  stops: MapStop[];
  roundTrip: boolean;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const [hovered, setHovered] = useState<string | null>(null);

  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  useEffect(reset, [stops.length]);

  const projectFlat = useMemo(() => {
    if (stops.length === 0) return () => ({ x: WIDTH / 2, y: HEIGHT / 2 });

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const point of stops) {
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
    }
    // Bei einem einzelnen Punkt (oder einer exakten Linie) fehlt die Spanne.
    if (maxLat - minLat < 0.002) {
      minLat -= 0.01;
      maxLat += 0.01;
    }
    if (maxLng - minLng < 0.002) {
      minLng -= 0.01;
      maxLng += 0.01;
    }

    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;

    return (lat: number, lng: number) => ({
      x: PADDING + ((lng - minLng) / lngSpan) * (WIDTH - 2 * PADDING),
      y: PADDING + (1 - (lat - minLat) / latSpan) * (HEIGHT - 2 * PADDING),
    });
  }, [stops]);

  const segments = useMemo(() => {
    const list: { key: string; x1: number; y1: number; x2: number; y2: number; midX: number; midY: number; km?: number }[] = [];
    for (let index = 0; index < stops.length - 1; index += 1) {
      const from = projectFlat(stops[index].lat, stops[index].lng);
      const to = projectFlat(stops[index + 1].lat, stops[index + 1].lng);
      list.push({
        key: `leg-${index}`,
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
        midX: (from.x + to.x) / 2,
        midY: (from.y + to.y) / 2,
        km: stops[index + 1].distanceFromPrev,
      });
    }
    if (roundTrip && stops.length > 2) {
      const from = projectFlat(stops[stops.length - 1].lat, stops[stops.length - 1].lng);
      const to = projectFlat(stops[0].lat, stops[0].lng);
      list.push({
        key: 'leg-return',
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
        midX: (from.x + to.x) / 2,
        midY: (from.y + to.y) / 2,
      });
    }
    return list;
  }, [stops, projectFlat, roundTrip]);

  const active = stops.find((stop) => stop.key === (hovered ?? selectedKey)) ?? null;

  return (
    <>
      <div className="route-map-toolbar">
        <button className="btn sm icon-only" title="Vergrößern" onClick={() => setZoom((value) => Math.min(value * 1.25, 8))}>
          <IconZoomIn width={15} height={15} />
        </button>
        <button className="btn sm icon-only" title="Verkleinern" onClick={() => setZoom((value) => Math.max(value / 1.25, 0.5))}>
          <IconZoomOut width={15} height={15} />
        </button>
        <button className="btn sm icon-only" title="Ansicht zurücksetzen" onClick={reset}>
          <IconMaximize width={15} height={15} />
        </button>
      </div>

      <div
        className={`route-map-canvas ${dragging ? 'dragging' : ''}`}
        onPointerDown={(event) => {
          setDragging(true);
          dragOrigin.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!dragging) return;
          setPan({ x: event.clientX - dragOrigin.current.x, y: event.clientY - dragOrigin.current.y });
        }}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
      >
        {stops.length === 0 ? (
          <div className="empty-state" style={{ paddingTop: 120 }}>
            <span className="empty-icon">
              <IconNavigation width={22} height={22} />
            </span>
            <strong>Noch keine Tour</strong>
            <div className="small">
              Fügen Sie links mindestens zwei Stopps hinzu – die Karte zeichnet die Reihenfolge automatisch.
            </div>
          </div>
        ) : (
          <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Schematische Darstellung der Tour">
            <defs>
              <pattern id="route-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--map-grid)" strokeWidth="1" />
              </pattern>
              <marker id="route-arrow" viewBox="0 0 10 10" refX="26" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 1 L 10 5 L 0 9 z" fill="var(--primary)" />
              </marker>
            </defs>
            <rect width="100%" height="100%" fill="url(#route-grid)" />

            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {segments.map((segment) => (
                <g key={segment.key}>
                  <line
                    x1={segment.x1}
                    y1={segment.y1}
                    x2={segment.x2}
                    y2={segment.y2}
                    stroke="var(--primary)"
                    strokeOpacity={0.22}
                    strokeWidth={5}
                    strokeLinecap="round"
                  />
                  <line
                    x1={segment.x1}
                    y1={segment.y1}
                    x2={segment.x2}
                    y2={segment.y2}
                    stroke="var(--primary)"
                    strokeWidth={2.4}
                    strokeLinecap="round"
                    strokeDasharray={segment.key === 'leg-return' ? '3, 7' : '7, 5'}
                    markerEnd="url(#route-arrow)"
                  />
                  {segment.km != null && segment.km > 0 && (
                    <g transform={`translate(${segment.midX}, ${segment.midY})`}>
                      <rect x="-32" y="-10" width="64" height="19" rx="9" fill="var(--surface)" stroke="var(--border)" />
                      <text y="3" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="var(--primary-text)">
                        {formatNumber(segment.km, 2)} km
                      </text>
                    </g>
                  )}
                </g>
              ))}

              {stops.map((stop, index) => {
                const coords = projectFlat(stop.lat, stop.lng);
                const isSelected = selectedKey === stop.key || hovered === stop.key;
                const isStart = index === 0;
                const isEnd = index === stops.length - 1 && !roundTrip;
                const fill = isStart ? 'var(--success)' : isEnd ? 'var(--danger)' : 'var(--primary)';

                return (
                  <g
                    key={stop.key}
                    transform={`translate(${coords.x}, ${coords.y})`}
                    style={{ cursor: 'pointer' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect?.(stop.key === selectedKey ? null : stop.key);
                    }}
                    onMouseEnter={() => setHovered(stop.key)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    {(isStart || isSelected) && <circle r={21} fill="none" stroke={fill} strokeWidth="2" opacity={0.35} />}
                    <circle r={isSelected ? 15 : 12.5} fill={fill} stroke="var(--surface)" strokeWidth="2.5" />
                    <text textAnchor="middle" y="4" fontSize={isSelected ? 11.5 : 10} fontWeight="800" fill="#fff">
                      {index + 1}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>

      {active && <StopDetail stop={active} stops={stops} roundTrip={roundTrip} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Gemeinsame Fußzeile                                                 */
/* ------------------------------------------------------------------ */

function StopDetail({ stop, stops, roundTrip }: { stop: MapStop; stops: MapStop[]; roundTrip: boolean }) {
  const index = stops.indexOf(stop);
  return (
    <div className="route-map-detail">
      <span className="icon-tile primary">
        <IconPin width={17} height={17} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row" style={{ gap: 8 }}>
          <span className="section-title">Stopp {index + 1}</span>
          {index === 0 && <span className="badge success">Start</span>}
          {index === stops.length - 1 && !roundTrip && <span className="badge danger">Ziel</span>}
        </div>
        <strong className="truncate" style={{ display: 'block', fontSize: 13.5 }}>
          {stop.label}
        </strong>
        <div className="small subtle truncate">
          {stop.address || `${stop.lat.toFixed(5)} / ${stop.lng.toFixed(5)}`}
        </div>
      </div>
    </div>
  );
}
